import { constructors } from '.';
import ViewModelDataCollector from '../ScriptEngine/ViewModelDataCollector';
import { DynamicFormPartType } from '../Types';
import BaseViewModel, { BaseViewModelParams } from './BaseViewModel';
import FormViewModel from './FormViewModel';
import {
	isIndexable,
	resolveContextValueByPaths,
} from '../../../Utils/ContextUtils';

// тип для элемента коллекции
type CollectionElementNode = {
	LocalPath?: string;
	Value?: unknown;
	selectedValueS2?: unknown;
	metaData?: unknown[];
	options?: unknown[];
	FormElements?: CollectionElementNode[];
};

// тип для сохранения metaData/options для выбранных dictionary-элементов
type DictMetaState = {
	metaData: unknown[];
	options: unknown[];
};

class CollectionViewModel extends BaseViewModel {
	// Шаблон элемента коллекции — форма
	itemToAdd: FormViewModel | null = null;
	ButtonCaption: string;
	CollectionItems: Array<FormViewModel> = [];
	MaxItemsCount = 0;
	ShowAddItemsButton = !!this.MaxItemsCount;
	temporaryFilesArray: Array<any> = [];
	isReadOnly = false;

	constructor(params: BaseViewModelParams) {
		super(params);
		this.Tooltip = params.elementDescriptor.Tooltip;
		this.ButtonCaption =
			params.elementDescriptor.ButtonCaption ?? 'Добавить';
		this.isReadOnly = BaseViewModel.isReadOnly;
	}

	// уточняем тип: у элемента есть FormElements
	private hasFormElements = (
		element: BaseViewModel,
	): element is BaseViewModel & { FormElements: BaseViewModel[] } => {
		return Array.isArray(
			(element as BaseViewModel & { FormElements?: BaseViewModel[] })
				.FormElements,
		);
	};

	setValue = (value: unknown) => {
		this.Value = value;

		if (!value) {
			this.nullateProperties(
				BaseViewModel.EntityPathToViewModel,
				this.FullPath,
			);
			this.CollectionItems.length = 0; //.removeAll();
			return;
		}
		this.CollectionItems.length = 0;

		if (Array.isArray(value)) {
			for (let i = 0; i < value.length; i++) {
				if (this.MaxItemsCount === 0 || i < this.MaxItemsCount) {
					const childValue = value[i];
					const item = this.addItem(i, childValue);
					this.setValueForChild(item, childValue);
				}
			}
		} else {
			this.addItem(undefined, value);
		}
	};

	setValueForChild = (child: BaseViewModel, value: unknown) => {
		if (!isIndexable(value) || !this.hasFormElements(child)) {
			return;
		}
		child.FormElements.forEach((formElement) => {
			const localPath = formElement.LocalPath;
			if (!localPath || localPath === '.') {
				return;
			}

			if (this.hasFormElements(formElement)) {
				const nestedValue = resolveContextValueByPaths(
					value,
					localPath,
					formElement.Path,
				);
				if (nestedValue) {
					this.setValueForChild(formElement, nestedValue);
				}
			} else if (
				(
					formElement as BaseViewModel & {
						CollectionItems?: FormViewModel[];
					}
				).CollectionItems
			) {
				const collectionData = resolveContextValueByPaths(
					value,
					localPath,
					formElement.Path,
				);
				if (
					Array.isArray(collectionData) &&
					typeof formElement.setValue === 'function'
				) {
					formElement.setValue(collectionData);
				}
			} else {
				const val = resolveContextValueByPaths(
					value,
					localPath,
					formElement.Path,
				);
				if (val !== undefined && val !== null) {
					if (!(
						formElement.elementDescriptor.DescriptorKey ===
							'TextControlDescriptor' && typeof val !== 'string'
					)) {
						formElement.setValue(val);
					}
				}
			}
		});
	};

	// Убирает элементы коллекции, вышедшие за новый лимит MaxItemsCount:
	// при уменьшении лимита скриптом (например, при включении оплаты через СБП, где, допустим, только один УИН)
	// лишние элементы должны исчезнуть из формы, иначе на сервер уйдут их значения
	// Лимит 0 означает "без ограничения" - в этом случае ничего не удаляем
	trimItemsToMaxCount = (maxItemsCount: number) => {
		const currentCount = this.CollectionItems.length;
		if (maxItemsCount <= 0 || currentCount <= maxItemsCount) {
			return;
		}
		this.CollectionItems.splice(
			maxItemsCount,
			currentCount - maxItemsCount,
		);
		// обнуляем модели удалённых элементов, чтобы скрипты не работали с уже несуществующими путями
		for (let i = maxItemsCount; i < currentCount; i++) {
			this.nullateProperties(
				BaseViewModel.EntityPathToViewModel,
				this.FullPath + '[' + i + ']',
			);
		}
	};

	checkShowAddItemsButton = (count?: number): boolean => {
		const currentCount =
			count !== undefined ? count : this.CollectionItems.length;
		const maxItemsCount = Number(this.MaxItemsCount) || 0;
		if (!this.ControlEnabling) {
			this.ShowAddItemsButton = false;
			return this.ShowAddItemsButton;
		}
		const filledCount =
			maxItemsCount > 0 ? this.getFilledItemsCount() : currentCount;
		if (maxItemsCount > 0 && filledCount >= maxItemsCount) {
			this.ShowAddItemsButton = false;
		} else {
			this.ShowAddItemsButton = true;
		}
		return this.ShowAddItemsButton;
	};

	private getFilledItemsCount = (): number => {
		let count = 0;
		for (let i = 0; i < this.CollectionItems.length; i++) {
			const item = this.CollectionItems[i];
			const data =
				ViewModelDataCollector.collectCurrentElementDataFromKoVm(item);
			if (this.hasMeaningfulValue(data)) {
				count += 1;
			}
		}
		return count;
	};

	private hasMeaningfulValue = (value: unknown): boolean => {
		if (value === null || value === undefined) {
			return false;
		}
		if (typeof value === 'string') {
			return value.trim().length > 0;
		}
		if (typeof value === 'number') {
			return true;
		}
		if (typeof value === 'boolean') {
			return value === true;
		}
		if (Array.isArray(value)) {
			return (value as unknown[]).some((v) => {
				return this.hasMeaningfulValue(v);
			});
		}
		if (typeof value === 'object') {
			return Object.values(value as Record<string, unknown>).some((v) => {
				return this.hasMeaningfulValue(v);
			});
		}
		return true;
	};

	// функция добавления
	addItem = (index?: number, formContext?: unknown) => {
		const collectionIndex =
			index !== undefined ? index : this.CollectionItems.length;
		const businessEntityPath = this.Path;
		const viewModelPath = this.viewModelPath;
		const elementDescriptor =
			this.elementDescriptor.CollectionElementDescriptor;
		const resolvedFormObject = formContext;
		const newForm = new FormViewModel({
			elementDescriptor: elementDescriptor!,
			parent: this,
			viewModelPath,
			businessEntityPath,
			formObject: resolvedFormObject ?? {},
			collectionIndex,
		});

		if (this.elementDescriptor?.CollectionElementDescriptor?.FormElements) {
			const recursionContext = resolvedFormObject ?? formContext;
			this.elementDescriptor.CollectionElementDescriptor.FormElements.forEach(
				(descriptor) => {
					newForm.FormElements.push(
						this.addItemCollectionRecursion(
							descriptor,
							this.itemToAdd,
							newForm,
							recursionContext,
						),
					);
				},
			);
			if (recursionContext && typeof recursionContext === 'object') {
				this.setValueForChild(newForm, recursionContext);
			}
		} else {
			console.warn(
				'Warn: CollectionElementDescriptor: ' +
					this.elementDescriptor?.CollectionElementDescriptor
						?.DescriptorKey,
			);
		}

		this.CollectionItems.push(newForm);

		// для того чтобы элементы добавились в разметку (сейчас по ним рассчитывается путь)
		if (BaseViewModel.isClientScripts !== false) {
			setTimeout(() => {
				return BaseViewModel.mediator.onViewModelBuilded(newForm);
			}, 0);
		}

		return newForm;
	};

	addItemCollectionRecursion = (
		descriptor: DynamicFormPartType,
		itemToAdd: BaseViewModel | null,
		parent: BaseViewModel,
		formContext?: unknown,
	) => {
		const self = this;
		const Constructor = constructors[descriptor.DescriptorKey];
		// Получаем заполненные данные формы
		const ctx =
			formContext &&
			isIndexable(formContext) &&
			Object.hasOwn(formContext, descriptor.LocalPath)
				? (formContext as Record<string, unknown>)[descriptor.LocalPath]
				: undefined;
		const tempoElement: any = new Constructor({
			elementDescriptor: descriptor,
			parent,
			formObject: ctx,
			viewModelPath: descriptor.viewModelPath,
			businessEntityPath: descriptor.Path,
		}) as BaseViewModel & {
			FormElements?: BaseViewModel[];
			CollectionItems?: FormViewModel[];
			itemToAdd?: BaseViewModel | null;
			IsMandatory?: boolean;
			setValue?: (value: unknown) => void;
			addItem?: () => void;
		};

		// Это форма
		if (descriptor?.FormElements?.length) {
			descriptor.FormElements.forEach((element: DynamicFormPartType) => {
				const el = self.addItemCollectionRecursion(
					element,
					null,
					tempoElement,
					ctx,
				);
				tempoElement.FormElements.push(el);
			});
		}

		if (descriptor.CollectionElementDescriptor !== undefined) {
			//Это коллекция
			const collection = tempoElement;

			// костыль Для получения itemToAdd
			const path = (collection.FullPath + '[0]').replace(
				/\[\d+\]/g,
				'[0]',
			);
			// TODO - self.dynamicFormHub.EntityPathToViewModelRender[path];
			collection.itemToAdd = BaseViewModel.EntityPathToViewModel[path];

			if (
				BaseViewModel.isClientScripts !== false ||
				BaseViewModel.formWasLoaded === true
			) {
				if (!collection.CollectionItems) {
					collection.CollectionItems = [];
				}
				const count = collection.CollectionItems.length;
				if (
					count === 0 &&
					collection.IsMandatory === true &&
					typeof collection.addItem === 'function'
				) {
					collection.addItem();
				}
			}
		}

		if (descriptor.DescriptorKey === 'EnumControlDescriptor') {
			self.setValueIfItExist(
				tempoElement,
				'DictionaryId',
				descriptor.DictionaryId,
				itemToAdd,
			);
			self.setValueIfItExist(
				tempoElement,
				'ParentId',
				descriptor.ParentId,
				itemToAdd,
			);
		} else if (
			descriptor.DescriptorKey === 'DictionaryRegistryControlDescriptor'
		) {
			self.setValueIfItExist(
				tempoElement,
				'DictionaryId',
				descriptor.DictionaryId,
				itemToAdd,
			);
			self.setValueIfItExist(
				tempoElement,
				'ParentId',
				descriptor.ParentId,
				itemToAdd,
			);
			self.setValueIfItExist(
				tempoElement,
				'DictionarySource',
				descriptor.DictionarySource,
				itemToAdd,
			);
			self.setValueIfItExist(
				tempoElement,
				'DisplayFieldScript',
				descriptor.DisplayFieldScript,
				itemToAdd,
			);
		}

		self.setValueIfItExist(
			tempoElement,
			'MaxValue',
			descriptor.MaxValue,
			itemToAdd,
		);
		self.setValueIfItExist(
			tempoElement,
			'MinValue',
			descriptor.MinValue,
			itemToAdd,
		);

		if (
			descriptor.DescriptorKey !== 'FiasRegionSelectControlViewModel' &&
			descriptor.DescriptorKey !== 'FIAS'
		) {
			tempoElement.IsMandatory = descriptor.IsMandatory === true;
		}

		// ежели есть данные для элемента формы, то
		if (ctx !== undefined) {
			if (typeof tempoElement?.setValue === 'function') {
				tempoElement.setValue(ctx);
			} else {
				tempoElement.Value = ctx;
			}
		}
		return tempoElement;
	};

	addItemOnServer = () => {
		// const form = this.addItem(null);
		//если руби то надо добавить элемент на стороне сервера
		if (BaseViewModel.isClientScripts !== true) {
			//Отправить на сервер уведомление, что был добавлен элемент коллекции
			// this.requestArgs.Path = this.getFullElementPath( this.element(), this.Path );
			// this.requestArgs.Value = "add#" + (this.CollectionItems.length);
			// form.disableShowErrors();
			// params.dynamicFormHub.updateValue(this.requestArgs).done(function () {
			// 	form.enableShowErrors();
			// });
		}
	};

	setValueIfItExist = (
		tempoElement: any,
		field: string,
		value: any,
		itemToAdd?: any,
	) => {
		if (value) {
			tempoElement[field] = value;
		} else if (
			itemToAdd &&
			Array.isArray((itemToAdd as any).FormElements)
		) {
			// Если в дескрипторе нет данных, поищем их в шаблонном клоне
			const prevFormElements = (itemToAdd as any)
				.FormElements as Array<any>;
			for (let num = 0; num < prevFormElements.length; num++) {
				if (prevFormElements[num]?.Path === tempoElement.Path) {
					if (
						prevFormElements[num][field] !== null &&
						prevFormElements[num][field] !== undefined
					) {
						tempoElement[field] = prevFormElements[num][field];
						break;
					}
				}
			}
		}
	};

	remove = (index: number) => {
		if (this.IsMandatory && this.CollectionItems.length <= 1) {
			return;
		}
		this.itemToAdd = { ...this.CollectionItems[index] };
		this.CollectionItems.splice(index, 1);
		this.updateEntities(this.FullPath);
		// для "обязательной" коллекции первый элемент "обновляется"
		if (this.IsMandatory && this.CollectionItems.length === 0) {
			this.addItem();
			return;
		}
		// если руби то надо удалить элемент на стороне сервера
		if (BaseViewModel.isClientScripts !== true) {
			// self.requestArgs.Path = self.getFullElementPath(self.element(), self.Path);
			// self.requestArgs.Value = "delete#" + (index);
			// params.dynamicFormHub.updateValue(self.requestArgs);
		} else if (this.CollectionItems.length > 0) {
			this.recreateCollectionAfterRemove();
		}
	};

	updateEntities = (removedName: string) => {
		// Обнуление моделей удалённого клона и сдвиг следующих на его место
		const startNameTerm = this.FullPath + '[0]';
		const objectToClean = BaseViewModel.EntityPathToViewModel;
		// Обнуление моделей удалённого клона и сдвиг следующих на его место
		for (const x in objectToClean) {
			if (
				objectToClean.hasOwnProperty(x) &&
				x.lastIndexOf(startNameTerm, 0) === 0
			) {
				delete objectToClean[x];
				for (let i = 1; i < 100; i++) {
					const nextNamePart = removedName + '[' + i + ']';
					const nextName = x.replace(startNameTerm, nextNamePart);
					if (objectToClean.hasOwnProperty(nextName)) {
						delete objectToClean[nextName];
					} else {
						break;
					}
				}
			}
		}
	};

	recreateCollectionAfterRemove = () => {
		const isClientScripts = BaseViewModel.isClientScripts !== false;

		// Сохраняем selectedValueS2 (LV) до пересборки - collectCurrentElementDataFromKoVm
		// конвертируем LV -> UV для EnumControl, и после setValueForChild модель получает UV
		// UI использует LV как id в options для SimpleSelect
		const savedValues: Array<Map<string, unknown>> = [];
		const dictMeta = new Map<string, DictMetaState>();
		this.CollectionItems.forEach((item, idx) => {
			const itemValues = new Map<string, unknown>();
			this.saveSelectedValues(
				item as unknown as CollectionElementNode,
				itemValues,
			);
			savedValues[idx] = itemValues;
			this.saveDictMeta(
				item as unknown as CollectionElementNode,
				dictMeta,
			);
		});

		const contextData =
			ViewModelDataCollector.collectCurrentElementDataFromKoVm(this, {
				saveAttachments: isClientScripts,
			});
		this.CollectionItems.length = 0;
		for (let index = 0; index < contextData.length; index++) {
			const data = contextData[index];
			this.addItem(index, data);
		}

		// восстанавливаем оригинальные LV-значения поверх UV, выставленных setValueForChild
		this.CollectionItems.forEach((item, idx) => {
			if (savedValues[idx]) {
				this.restoreSelectedValues(
					item as unknown as CollectionElementNode,
					savedValues[idx],
				);
			}
			this.restoreDictMeta(
				item as unknown as CollectionElementNode,
				dictMeta,
			);
		});
	};

	// рекурсивно сохраняем текущие selectedValueS2 по LocalPath до пересборки коллекции
	private saveSelectedValues = (
		element: CollectionElementNode,
		valueMap: Map<string, unknown>,
	) => {
		if (element.selectedValueS2 !== undefined) {
			valueMap.set(element.LocalPath ?? '', element.selectedValueS2);
		}
		if (Array.isArray(element.FormElements)) {
			element.FormElements.forEach((child) => {
				return this.saveSelectedValues(child, valueMap);
			});
		}
	};

	// рекурсивно возвращаем selectedValueS2/Value после пересоздания элементов коллекции
	private restoreSelectedValues = (
		element: CollectionElementNode,
		valueMap: Map<string, unknown>,
	) => {
		if (element.selectedValueS2 !== undefined) {
			const saved = valueMap.get(element.LocalPath ?? '');
			if (saved !== undefined) {
				element.selectedValueS2 = saved;
				element.Value = saved;
			}
		}
		if (Array.isArray(element.FormElements)) {
			element.FormElements.forEach((child) => {
				return this.restoreSelectedValues(child, valueMap);
			});
		}
	};

	// сохраняем metaData/options для выбранных dictionary-элементов, чтобы не потерять их после пересборки
	private saveDictMeta = (
		element: CollectionElementNode,
		metaMap: Map<string, DictMetaState>,
	) => {
		if (
			element.selectedValueS2 !== undefined &&
			Array.isArray(element.metaData) &&
			element.metaData.length > 0
		) {
			const key = `${element.LocalPath}:${element.selectedValueS2}`;
			metaMap.set(key, {
				metaData: [...element.metaData],
				options: Array.isArray(element.options)
					? [...element.options]
					: [],
			});
		}
		if (Array.isArray(element.FormElements)) {
			element.FormElements.forEach((child) => {
				return this.saveDictMeta(child, metaMap);
			});
		}
	};

	// восстанавливаем ранее сохраненные metaData/options, если у нового элемента они пустые
	private restoreDictMeta = (
		element: CollectionElementNode,
		metaMap: Map<string, DictMetaState>,
	) => {
		if (
			element.selectedValueS2 !== undefined &&
			Array.isArray(element.metaData) &&
			!element.metaData.length
		) {
			const key = `${element.LocalPath}:${element.selectedValueS2}`;
			const saved = metaMap.get(key);
			if (saved) {
				element.metaData = saved.metaData;
				element.options = saved.options;
			}
		}
		if (Array.isArray(element.FormElements)) {
			element.FormElements.forEach((child) => {
				return this.restoreDictMeta(child, metaMap);
			});
		}
	};

	override Validate = () => {
		let valid = true;
		for (let i = 0; i < this.CollectionItems.length; i++) {
			valid = this.CollectionItems[i].Validate() && valid;
		}
		return valid;
	};

	// SimpleFormat
	getSimpleFormatXmlTagName = () => {
		return 'Collection';
	};

	getSimpleFormatXmlOpen = () => {
		let xml =
			'<' +
			this.getSimpleFormatXmlTagName() +
			' idRef="' +
			this.LocalPath +
			'" title="' +
			this.elementDescriptor.Caption +
			'" itemsCount="' +
			this.CollectionItems.length +
			'" ';
		if (this.IsMandatory) {
			xml += 'isRequired="true"';
		}
		return xml + '>';
	};
}

export default CollectionViewModel;
